// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import * as echarts from 'echarts/core';
import { ChartSeriesConfig } from '../interfaces/chart-config.interface';
export type EChartsOption = echarts.EChartsCoreOption;
import { EChartsTooltipParam } from '../interfaces/graph-config.interface';

const COLOR_CONFIG = {
  airHot: '#B21B5C',
  airCold: '#3DC3B0',
  waterIn: '#006ADD',
  waterOut: '#F19E6A',
  waterFlow: '#6E7B9F',
  threshold: '#6E7B9F',
  thresholdAlert: '#6E7B9F',
  humidity: '#666666',
  dewPoint: '#C8C8C8',
};

export const PARAMETER_CONFIG: { [key: string]: ChartSeriesConfig } = {
  WaterFlow: {
    name: 'Water Flow',
    type: 'line',
    step: 'middle',
    smooth: true,
    data: [],
    large: true,
    itemStyle: {
      color: COLOR_CONFIG.waterFlow,
    },
    emphasis: {
      focus: 'series',
      blurScope: 'coordinateSystem',
    },
  },
  Threshold: {
    name: 'Threshold',
    type: 'line',
    step: 'middle',
    symbol: 'none',
    color: COLOR_CONFIG.threshold,
    symbolSize: 7,
    data: [],
    large: true,
    lineStyle: {
      color: COLOR_CONFIG.threshold,
      width: 1.5,
      type: 'dashed',
    },
    emphasis: {
      focus: 'series',
      blurScope: 'coordinateSystem',
    },
  },
  'Threshold Alert': {
    name: 'Threshold Alert',
    type: 'line',
    step: 'middle',
    symbol: 'arrow',
    color: COLOR_CONFIG.thresholdAlert,
    symbolSize: 7,
    data: [],
    large: true,
    lineStyle: {
      color: COLOR_CONFIG.threshold,
      width: 1.5,
      type: 'dashed',
    },
    emphasis: {
      focus: 'series',
      blurScope: 'coordinateSystem',
    },
  },
  AirHot: {
    name: 'Air Hot',
    type: 'line',
    smooth: true,
    step: 'middle',
    yAxisIndex: 1,
    data: [],
    large: true,
    itemStyle: {
      color: COLOR_CONFIG.airHot,
    },
    emphasis: {
      focus: 'series',
      blurScope: 'coordinateSystem',
    },
  },
  AirCold: {
    name: 'Air Cold',
    type: 'line',
    smooth: true,
    step: 'middle',
    yAxisIndex: 1,
    data: [],
    large: true,
    itemStyle: {
      color: COLOR_CONFIG.airCold,
    },
    emphasis: {
      focus: 'series',
      blurScope: 'coordinateSystem',
    },
  },
  WaterIn: {
    name: 'Water In',
    type: 'line',
    smooth: true,
    step: 'middle',
    yAxisIndex: 1,
    data: [],
    large: true,
    itemStyle: {
      color: COLOR_CONFIG.waterIn,
    },
    emphasis: {
      focus: 'series',
      blurScope: 'coordinateSystem',
    },
  },
  WaterOut: {
    name: 'Water Out',
    type: 'line',
    smooth: true,
    step: 'middle',
    yAxisIndex: 1,
    data: [],
    large: true,
    itemStyle: {
      color: COLOR_CONFIG.waterOut,
    },
    emphasis: {
      focus: 'series',
      blurScope: 'coordinateSystem',
    },
  },
};

export function formatTooltip(item: EChartsTooltipParam): string {
  // Customize the tooltip content based on series name
  // You can modify this logic to fit your specific needs
  const ParametersWithLiters = ['Water Flow', 'Threshold', 'Threshold Alert'];
  const ParametersWithCelsius = [
    'Air Cold',
    'Air Hot',
    'Water In',
    'Water Out',
  ];
  if (ParametersWithLiters.includes(item.seriesName)) {
    const data = item.data[1] === null ? '-' : item.data[1];
    item.data = data + ' l/h';
  }

  if (ParametersWithCelsius.includes(item.seriesName)) {
    const data = item.data[1] === null ? '-' : Number(item.data[1]).toFixed(1);
    item.data = data + ' °C';
  }
  return `${item.marker} ${item.seriesName}: ${item.data}`;
}

export function toolTipFormatter(params: EChartsTooltipParam[]): string {
  // Extract the default tooltip content for series
  const rawtooltip = params
    .map((item: EChartsTooltipParam) => {
      return formatTooltip(item);
    })
    .join('<br/>');

  // Modify the x-axis label from params[0].axisValue as needed
  const dateTime = params[0].value[0];
  const formattedDateTime =
    typeof dateTime === 'string'
      ? dateTime.replace('T', ' ')
      : new Date(dateTime).toISOString().replace('T', ' ');
  const displayTime = `Event: ${formattedDateTime}`;

  return displayTime + '<br/>' + rawtooltip;
}

export const DEFAULT_GRAPH_CONFIG: EChartsOption = {
  dataZoom: [
    {
      type: 'slider',
      xAxisIndex: 0,
      filterMode: 'filter',
      start: 0,
      end: 100,
      bottom: 20,
    },
    {
      type: 'inside',
      xAxisIndex: 0,
    },
  ],

  toolbox: {
    feature: {
      dataZoom: {
        yAxisIndex: false,
      },
      saveAsImage: {
        pixelRatio: 2,
      },
    },
  },

  legend: {
    show: true,
    top:'top',
  },

  grid: {
    left: '3%',
    right: '4%',
    bottom: '15%',
    containLabel: true,
  },
};
